import { useControllableState } from "@faire/slate/private/hooks/useControllableState";
import {
  useFloatingRootContext,
  UseInteractionsReturn,
  OpenChangeReason,
} from "@floating-ui/react";
import React, { createContext, useContext, useMemo, useState } from "react";

export type IFloatingOptions = {
  /**
   * Whether the floating element is initially open. Used when the floating element is uncontrolled.
   */
  initiallyOpen?: boolean;
  /**
   * Whether the floating element is open. Used when the floating element is controlled.
   */
  open?: boolean;
  /**
   * The callback function that is called when the floating element is opened or closed.
   */
  onOpenChange?: (open: boolean, reason?: OpenChangeReason) => void;
};

export const FloatingContext: React.FC<
  React.PropsWithChildren<IFloatingOptions>
> = ({ children, ...props }) => {
  const value = useInitFloatingContext(props);
  return <Context value={value}>{children}</Context>;
};

export const useFloatingContext = () => {
  const context = useContext(Context);
  if (!context) {
    throw new Error("FloatingContext not found");
  }
  return context;
};

const Context = createContext<
  ReturnType<typeof useInitFloatingContext> | undefined
>(undefined);

const useInitFloatingContext = ({
  initiallyOpen,
  open: controlledOpen,
  onOpenChange,
}: IFloatingOptions) => {
  const [open, setOpen] = useControllableState({
    initialValue: initiallyOpen,
    value: controlledOpen,
  });

  const [reference, setReference] = useState<Element | null>(null);
  const [floating, setFloating] = useState<HTMLElement | null>(null);
  const [floatingStyles, setFloatingStyles] = useState<React.CSSProperties>({});
  const [propGetters, setPropGetters] = useState<UseInteractionsReturn>({
    getReferenceProps: (props) => ({ ...props }),
    getFloatingProps: (props) => ({ ...props }),
    getItemProps: (props) => ({ ...props }),
  });

  const floatingRootContext = useFloatingRootContext({
    open,
    onOpenChange: (open, _, reason) => {
      setOpen(open);
      onOpenChange?.(open, reason);
    },
    elements: {
      reference: reference,
      floating: floating,
    },
  });

  return useMemo(
    () => ({
      floatingRootContext,
      open: floatingRootContext.open,
      setOpen: floatingRootContext.onOpenChange,
      floatingStyles,
      setFloatingStyles,
      refs: {
        reference,
        floating,
        setReference,
        setFloating,
      },
      propGetters,
      setInteractions: setPropGetters,
    }),
    [floatingRootContext, reference, floating, floatingStyles, propGetters]
  );
};
